// KPI Builder — open-ended answers. The catalogue suggests fields, but the admin can name any field,
// condition, data set or column, so a KPI is not limited to what has been catalogued.
import { SOURCES } from 'constants/kpiBuilderCatalogue';
import { BuilderAnswers, CustomAnswers, CustomFilter, DataSource, FilterOp, GroupBy, Measure, SourceId } from './types';

type Clause = Record<string, unknown>;
interface Parts { must?: Clause; mustNot?: Clause; }

export const FILTER_OPS: { id: FilterOp; label: string; values: 0 | 1 | 2 }[] = [
  { id: 'is', label: 'is', values: 1 }, { id: 'isNot', label: 'is not', values: 1 }, { id: 'oneOf', label: 'is one of (comma separated)', values: 1 },
  { id: 'exists', label: 'has a value', values: 0 }, { id: 'missing', label: 'is empty', values: 0 },
  { id: 'gt', label: 'is more than', values: 1 }, { id: 'gte', label: 'is at least', values: 1 },
  { id: 'lt', label: 'is less than', values: 1 }, { id: 'lte', label: 'is at most', values: 1 },
  { id: 'between', label: 'is between', values: 2 },
];

// Personal data can never be shown as a group or column, however it is spelled.
const PERSONAL = /phone|mobile|email|aadhaar|aadhar|registrationnumber|regnurse|professionalid|birth|dob/i;
export const isPersonalField = (field: string): boolean => PERSONAL.test(field);

/** Numbers and true/false are sent as such; everything else as text. */
const typed = (v: string): string | number | boolean => {
  const t = v.trim();
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t === 'true' ? true : t === 'false' ? false : t;
};

export const isFilterReady = (f: CustomFilter): boolean => {
  const need = FILTER_OPS.find((o) => o.id === f.op)?.values ?? 1;
  return f.field.trim() !== '' && (need < 1 || f.value.trim() !== '') && (need < 2 || f.value2.trim() !== '');
};

const range = (f: CustomFilter, bounds: Record<string, unknown>): Clause => ({ range: { [f.field]: bounds } });
const PARTS: Record<FilterOp, (f: CustomFilter) => Parts> = {
  is: (f) => ({ must: { term: { [f.field]: typed(f.value) } } }),
  isNot: (f) => ({ mustNot: { term: { [f.field]: typed(f.value) } } }),
  oneOf: (f) => ({ must: { terms: { [f.field]: f.value.split(',').filter((v) => v.trim() !== '').map(typed) } } }),
  exists: (f) => ({ must: { exists: { field: f.field } } }),
  missing: (f) => ({ mustNot: { exists: { field: f.field } } }),
  gt: (f) => ({ must: range(f, { gt: typed(f.value) }) }),
  gte: (f) => ({ must: range(f, { gte: typed(f.value) }) }),
  lt: (f) => ({ must: range(f, { lt: typed(f.value) }) }),
  lte: (f) => ({ must: range(f, { lte: typed(f.value) }) }),
  between: (f) => ({ must: range(f, { gte: typed(f.value), lte: typed(f.value2) }) }),
};

/** All finished conditions, split into what must match and what must not. */
export function splitFilters(filters: CustomFilter[]): { must: Clause[]; mustNot: Clause[] } {
  const parts = filters.filter(isFilterReady).map((f) => PARTS[f.op](f));
  return { must: parts.flatMap((p) => (p.must ? [p.must] : [])), mustNot: parts.flatMap((p) => (p.mustNot ? [p.mustNot] : [])) };
}

/** One condition as a single clause, for “who has this trait”. */
export function filterClause(f: CustomFilter): Clause {
  const { must, mustNot } = splitFilters([f]);
  return must[0] ?? { bool: { must_not: mustNot } };
}

/** Index names are stored without the environment prefix, so the same KPI works in dev and prod. */
export const stripEnvPrefix = (index: string): string => index.trim().replace(/^(dev|prod)-/, '');

export function customSource(c: CustomAnswers): DataSource {
  const s = c.source;
  const any = s.date || 'userId';
  return {
    id: 'custom', label: s.label || 'Other data', desc: 'Data you named yourself.', index: stripEnvPrefix(s.index), date: s.date, comp: s.comp || undefined,
    word: 'recorded', noLocation: !s.hasLocation, groups: [], cols: [],
    measures: [
      { id: 'records', label: 'Number of records', desc: 'Counts every record.', agg: 'value_count', field: any },
      { id: 'learners', label: 'Unique learners', desc: 'Counts each person once, by userId.', agg: 'cardinality', field: 'userId' },
    ],
  };
}

export const resolveSource = (id: SourceId, a: BuilderAnswers): DataSource => (id === 'custom' ? customSource(a.custom) : SOURCES[id]);

export function customMeasure(c: CustomAnswers): Measure | null {
  const m = c.measure;
  if (!m.field.trim()) return null;
  return { id: 'custom', label: m.label.trim() || 'Custom value', desc: 'A value you chose.', agg: m.agg, field: m.field.trim(), decimal: m.agg === 'avg' || m.divisor !== 1, divisor: m.divisor || 1 };
}

export function customGroup(c: CustomAnswers): GroupBy | null {
  const g = c.group;
  if (!g.field.trim() || isPersonalField(g.field)) return null;
  const label = g.label.trim() || g.field.trim();
  return { id: 'custom', label, field: g.field.trim(), cat: label };
}
